import { useState, useEffect } from 'react';
import { Box, Card, Typography, Grid, IconButton, ListItem, List, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Tooltip, Snackbar, Alert as MuiAlert } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import useSelection from 'hooks/useSelection';
import { useNavigate } from 'react-router';
import { formatDateWithTimezoneDisplay, isValidDateValue, formatDynamicScalar } from 'utils/dateFormat';
import { useRef } from 'react';
import useApi from 'hooks/useApi';
import KanbanMinimap from 'components/Minimapkanbanview';

// Type definitions
interface Item {
  id: string;
  ticket: string;
  location: string;
  date: string;
  time: string;
}

interface Column {
  count: number;
  name: string;
  items: Item[];
}

interface Columns {
  [key: string]: Column;
}

interface DashboardData {
  columns: Columns;
}


// Added statusFilter prop to support filtering by notification status
const PriorityTransaction= ({transactionData, loading, statusFilter = 'all',team,reportingStatus}: {
  transactionData: any; 
  loading: boolean;
  statusFilter?: 'all' | 'PRIORITY' | 'ALERT';  // Added to support 3-way filtering
  team: string;
  fetchPriorityTransactions?: () => void;
  reportingStatus:any;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { updateNotificationStatus } = useApi();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingTx, setPendingTx] = useState<any>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Add state to track acknowledged transactions
  const [acknowledgedTxIds, setAcknowledgedTxIds] = useState<Set<string>>(new Set());

  const groupByDisplayKeyStatus = (items: any[] = [], allStatuses: any[] = []) => {
    const groups: { [key: string]: any[] } = {};

  // Guard against null/undefined
    const statusList = allStatuses || [];

  // Initialize all statuses with empty arrays
    statusList.forEach(status => {
    const statusKey = typeof status === 'string' 
          ? status
      : (status?.displayName || status?.name || status?.label || 'Unknown');
      groups[statusKey] = [];
    });

  // Populate groups with actual items
    if (Array.isArray(items) && items?.length > 0) {
      items.forEach((item: any) => {
      const key = reportingStatus[item?.keyStatus] || "No Status";
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
    }


  // Natural sort with numeric awareness
  const sortedEntries = Object.entries(groups).sort(([keyA], [keyB]) => {
    return keyA.localeCompare(keyB, undefined, { 
      numeric: true, 
      sensitivity: 'base' 
    });
  });

    const initialData: DashboardData = {
      columns: sortedEntries.reduce((acc, [status, items]) => {
      acc[status] = {
        count: items.length,
        name: status,
        items,
      };
        return acc;
      }, {} as Columns),
    };

    setData(initialData);
    return groups;
  };

  const { selectedClient } = useSelection();
  const [data, setData] = useState<any>();
  const navigate = useNavigate();

  const handleAcknowledge = async (tx: any) => {
    if (tx?.notificationuid) {
      try {
        await updateNotificationStatus(tx.notificationuid, false);
        
        // Add the transaction ID to acknowledged set for immediate UI update
        setAcknowledgedTxIds(prev => new Set(prev).add(tx.uid || tx._id));
        
        setSnackbarMsg('Transaction acknowledged successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Optional: Call API refetch if you still want to sync with server
        // but the UI will already be updated
        // if (fetchPriorityTransactions) {
        //   await fetchPriorityTransactions();
        // }
      } catch (err) {
        setSnackbarMsg('Failed to acknowledge transaction');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };
  
  function formatToLocalTime(value: any) {
    return formatDateWithTimezoneDisplay(value); // No time, but includes timezone if available
  }

  const formatResourceValue = (value: any): string => {
    if (Array.isArray(value) && value.length > 0) {
      const firstItem = value[0];
      // Check if it's actually a resource object (has 'fields' property)
      if (typeof firstItem === 'object' && firstItem !== null && firstItem.fields && Object.keys(firstItem.fields).length > 0) {
        // Display first 2 resources
        const resourcesToShow = value.slice(0, 2);
        const resourceStrings = resourcesToShow.map((resource: any) => {
          if (resource.fields && Object.keys(resource.fields).length > 0) {
            // Show first 2 fields from each resource
            return Object.values(resource.fields)
              .slice(0, 2)
              .map(v => String(v))
              .join(', ');
          }
          return 'Resource';
        });

        const result = resourceStrings.join(' | ');
        return value.length > 2 ? result + ' ...' : result;
      }
    }
    // Not a resource array, return as-is for other handling
    return String(value);
  };

  // Filter rows based on statusFilter before grouping
  useEffect(() => {
    const filteredRows = transactionData.rows?.filter((row: any) => {
      if (!statusFilter || statusFilter === 'all') return true;
      // Handle both possible casing in the API response
      const status = (row.notificationStatusType || row.notificationstatusType || '').toString().toUpperCase();
      return status === statusFilter;
    }) || [];
    groupByDisplayKeyStatus(filteredRows);
  }, [transactionData, statusFilter, acknowledgedTxIds]); // Added acknowledgedTxIds dependency

  return (
    <>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MuiAlert elevation={6} variant="filled" onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
        {snackbarMsg}
      </MuiAlert>
    </Snackbar>

    <Box>
      
      <Box sx={{ overflowX: 'auto' }} >
        <Box
          ref={scrollContainerRef}
          sx={{
            maxHeight: '80vh',
            overflowX: 'auto',
            overflowY: 'auto',
            px: 0,
            py: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            '&::-webkit-scrollbar': {
              height: '5px',
              width: '5px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#D9D9D9CC',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
            '&::-webkit-scrollbar-button': {
              display: 'none',
            },
          }}
        >
          {(!selectedClient || !team || team === 'all') ? (
            <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
              <Typography variant="h5" color="text.secondary">
                {!selectedClient
                  ? "Please select a client and process to view priority transactions."
                  : team === 'all'
                    ? "Please select a particular process instead of 'All' to view priority transactions."
                    : "Please select a client and process to view priority transactions."}
              </Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
                Loading transactions...
              </Typography>
            </Box>
          ) : !data ? (
            <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
                Processing data...
              </Typography>
            </Box>
          ) : data?.columns ? Object.keys(data?.columns).length > 0 ? (
            <Grid container spacing={2} wrap="nowrap" sx={{ width: 'max-content', px: 2,background:"white" }} >
              {(
                data && data.columns && Object.keys(data.columns).length > 0 ? (() => {
                  const entries = Object.entries(data.columns);
                  const firstIndex = entries.findIndex(([key]) => key === 'First');
                  let ordered = [];
                  if (firstIndex !== -1) {
                    ordered = [entries[firstIndex], ...entries.slice(0, firstIndex), ...entries.slice(firstIndex + 1)];
                  } else {
                    ordered = entries;
                  }
                  return ordered.map(([columnId, column]) => {
                    const col = column as Column;
                    return (
                      <Grid item key={columnId} sx={{ p: 0,background:"white" }}>
                         <Box sx={{ 
                          position: 'sticky', 
                          top: 0, 
                          backgroundColor: 'white', 
                          zIndex: 10,
                          pb: 1,
                          pt: 1
                        }}>
                        <Typography variant="h4" sx={{ mb: 1, color: 'black', textAlign: 'center' }}>
                          {col.count}
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 1, color: 'black', textAlign: 'center',textTransform:"uppercase",fontWeight:"500" }}>
                          {col.name}
                        </Typography>
                         </Box>
                        <Box
                          sx={{
                            minHeight: 300,
                            borderRadius: 1,
                            paddingLeft: 0,
                            paddingRight: 0,
                          }}
                        >
                          {col.items.map((item: any) => (
                            <Card
                              onClick={() =>
                                navigate(`/transactions/details/${item?.team?.uid}/${item?.uid}/${item?.key}`)
                              }
                              key={item._id || item.id}
                              sx={{
                                mb: 2,
                                position: 'relative',
                                bgcolor: (theme) => item.notificationStatusType === 'ALERT'
                                  ? `${theme.palette.accent.dark}50`
                                  : item.notificationStatusType === 'PRIORITY'
                                    ? `${theme.palette.accent.main}50`
                                    : 'white !important',
                                boxShadow: 2,
                                borderRadius: 1,
                                width: 280,
                                p: 1,
                                pb: 6,
                                transition: "box-shadow 0.3s, transform 0.3s, background 0.3s",
                                cursor: "pointer",
                                border: '1px solid',
                                
                                borderColor: (theme) => item.notificationStatusType === 'ALERT'
                                  ? `${theme.palette.accent.dark}80`
                                  : item.notificationStatusType === 'PRIORITY'
                                    ? `${theme.palette.accent.main}80`
                                    : 'white !important',
                                "&:hover": {
                                  boxShadow: 6,
                                  // transform: "scale(1.04)",
                                  bgcolor: (theme) => item.notificationStatusType === 'ALERT'
                                  ? `${theme.palette.accent.dark}80`
                                  : item.notificationStatusType === 'PRIORITY'
                                    ? `${theme.palette.accent.main}80`
                                    : 'white !important',
                                },
                              }}
                            >
                              <Box>
                                <Grid container spacing={1} sx={{ p: 0 }}>
                                  {/* ✅ Dynamically render additionalFields */}
                                  {Object.entries(item?.additionalFields as Record<string, unknown> || {}).map(
                                    ([label, value]: [string, unknown], i: number) => (
                                      <Grid item xs={6} key={i}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                          sx={{ fontSize: "0.7rem", color: "text.secondary",textTransform:"uppercase" }}
                                        >
                                          {label}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                          sx={{ fontSize: "0.7rem", color: "text.primary",textTransform:"uppercase",fontWeight:"600" }}
                                        >
                                          {value !== undefined && value !== null && value !== ""
                                                ? Array.isArray(value)
                                                  ? (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'fields' in value[0])
                                                    ? formatResourceValue(value)
                                                    : value.map(v => formatDynamicScalar(v)).join(", ")
                                                  : isValidDateValue(value)
                                                    ? formatToLocalTime(value)
                                                    : String(value)
                                                : "-"}
                                        </Typography>
                                      </Grid>
                                    )
                                  )}


                                  {item?.team?.displayStepStatus &&
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontSize: "0.7rem", color: "text.secondary",textTransform:"uppercase" }}
                                      >
                                        Activity Status
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        sx={{ fontSize: "0.8rem", color: "text.primary" }}
                                      >
                                        {item?.displayStatus || "-"}
                                      </Typography>
                                    </Grid>
                                  }

                                  {item?.team?.reportingStatus && item?.keyStatus &&
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontSize: "0.7rem", color: "text.secondary",textTransform:"uppercase" }}
                                      >
                                        Status
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontSize: "0.8rem", color: "text.primary" }}
                                      >
                                        {reportingStatus[item?.keyStatus] || '-'}
                                      </Typography>
                                    </Grid>
                                  }
                                  {item?.team?.process &&
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontSize: "0.7rem", color: "text.secondary",textTransform:"uppercase" }}
                                      >
                                        Process
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        sx={{ fontSize: "0.8rem", color: "text.primary" }}
                                      >
                                        {item?.team?.name || '-'}
                                      </Typography>
                                    </Grid>
                                  }
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      sx={{ 
                                        font: "text.primary",
                                        fontSize: "0.7rem", 
                                        color: item.notificationStatusType === 'ALERT' 
                                          ? 'error.main'
                                          : 'error.main',
                                        textTransform:"uppercase"  
                                      }}
                                    >
                                      {item.notificationStatusType === 'ALERT' ? 'Alert Reason' : 'Notification Reason'}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      sx={{ fontSize: "0.75rem", color: "text.primary" }}
                                    >
                                      {item?.notificationReasons?.length ? (
                                        <List
                                          dense
                                          component="ol"
                                          sx={{
                                            listStyleType: "decimal",
                                            pl: 2,
                                            "& .MuiListItem-root": {
                                              display: "list-item",
                                              pl: 0,
                                              fontSize: "0.7rem",
                                              fontWeight:'600',
                                              py: 0.15,
                                            },
                                          }}
                                        >
                                          {item.notificationReasons.map((reason: string, i: number) => (
                                            <ListItem key={i}>{reason}</ListItem>
                                          ))}
                                        </List>
                                      ) : (
                                        <Typography variant="body2" color="text.primary">
                                          {item?.notificationTitle || "No reasons provided"}
                                        </Typography>
                                      )}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                              {/* File Attachment Icon - Outside Grid */}
                              {item?.file_Attachment && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 12,
                                    right: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <IconButton
                                    sx={{
                                      color: '#000000',
                                      bgcolor: 'background.paper',
                                      '&:hover': {
                                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                                      }
                                    }}
                                    size="small"
                                    // disabled
                                  >
                                    <AttachFileIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                              {/* Acknowledge Button */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 12,
                                  right: item?.file_Attachment ? 56 : 12,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Tooltip title="Acknowledge" placement="top">
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingTx(item);
                                      setConfirmOpen(true);
                                    }}
                                    sx={{
                                      bgcolor: 'success.main',
                                      color: 'white',
                                      '&:hover': {
                                        bgcolor: 'success.dark',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s',
                                      boxShadow: '0 2px 4px rgba(0, 127, 64, 0.2)'
                                    }}
                                    size="small"
                                  >
                                    <CheckCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Card>
                          ))}
                        </Box>
                      </Grid>
                    );
                  });
                })()
                  : (
                    ""
                  )
              )}
            </Grid>
          ) : <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'grey.300',
              minHeight: 200
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              No Priority Transaction Data are Present
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 1
              }}
            >
              <Typography>View All Transactions in Transaction Page</Typography>
            </Typography>
          </Box> : ""}
        </Box>
      </Box>

    </Box>
    {data?.columns && Object.keys(data.columns).length > 0 && (
        <KanbanMinimap 
          columns={data.columns} 
          containerRef={scrollContainerRef} 
        />
      )}

    {/* Confirmation Dialog */}
    <Dialog
      open={confirmOpen}
      onClose={() => {
        setConfirmOpen(false);
        setPendingTx(null);
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          bgcolor: "background.paper",
          boxShadow: "0px 6px 22px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1rem",
          textAlign: "left",
          pb: 1,
        }}
      >
        Confirm Acknowledge
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent sx={{ pt: 0, pb: 1 }}>
        <Stack spacing={1.4}>
          {/* Transaction Key */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {pendingTx?.team?.settings?.transactionLabel || "-"}:
            </Typography>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1.5,
                px: 0.8,
                py: 0.35,
                fontSize: "0.82rem",
                fontWeight: 600,
                bgcolor: "grey.50",
                minWidth: 60,
                textAlign: "center",
              }}
            >
              {pendingTx?.key || "-"}
            </Box>
          </Box>

          {/* Reasons */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {pendingTx?.notificationStatusType === "ALERT"
                ? "Alert Reasons"
                : "Priority Reasons"}
            </Typography>

            {pendingTx?.notificationReasons?.length ? (
              <List
                dense
                component="ol"
                sx={{
                  listStyleType: "decimal",
                  pl: 2,
                  "& .MuiListItem-root": {
                    display: "list-item",
                    pl: 0,
                    fontSize: "0.8rem",
                    py: 0.15,
                  },
                }}
              >
                {pendingTx.notificationReasons.map((reason: string, i: number) => (
                  <ListItem key={i}>{reason}</ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {pendingTx?.notificationTitle || "No reasons provided"}
              </Typography>
            )}
          </Box>

          {/* Note */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "0.78rem",
              textAlign: "left",
              mt: 0.5,
            }}
          >
            This transaction will be removed from your priority list. Do you wish to proceed?
          </Typography>
        </Stack>
      </DialogContent>

      {/* ACTION BUTTONS */}
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          px: 2,
          pt: 0.5,
          pb: 1,
        }}
      >
        <Button
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 2.2,
            bgcolor: "#007f40",
            "&:hover": { bgcolor: "#006b36" },
          }}
          onClick={async () => {
            setConfirmOpen(false);
            if (pendingTx) await handleAcknowledge(pendingTx);
            setPendingTx(null);
          }}
        >
          Yes, Acknowledge
        </Button>

        <Button
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 2.2,
          }}
          onClick={() => {
            setConfirmOpen(false);
            setPendingTx(null);
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default PriorityTransaction;